'use client';

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { PRISM_WORDMARK_PATH } from 'components/gifting/visuals/prismWordmark';
import { bebasNeue, geistMono } from 'utils/fonts';
import type { PatternFinish } from 'content/gifting/voucherConfig';
import {
  PATTERN_BG,
  PATTERN_FS,
  PATTERN_PRE,
  PATTERN_VS,
} from 'components/gifting/visuals/patternCardShaders';
import {
  createFullScreenProgram,
  drawFullScreen,
  loseGl,
} from 'components/gifting/visuals/glProgram';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { ArtCardFace } from './ArtCardFace';
import { attachCardTilt } from './cardTilt';
import type { CardProps } from './types';
import styles from './ArtCard.module.css';

// Label ink per pattern, from the design.
const WHITE = {
  '--tint': 'rgba(255,255,255,.88)',
  '--hi': 'rgba(255,255,255,.08)',
  '--sh': 'rgba(0,0,0,.6)',
} as CSSProperties;
const INK: Record<PatternFinish, CSSProperties> = {
  marble: {
    '--tint': 'rgba(222,255,238,.94)',
    '--hi': 'rgba(255,255,255,.16)',
    '--sh': 'rgba(0,40,20,.5)',
  } as CSSProperties,
  halftone: {
    '--tint': '#fff',
    '--hi': 'rgba(255,255,255,.15)',
    '--sh': 'rgba(0,0,60,.45)',
  } as CSSProperties,
  prism: WHITE,
};

type Props = CardProps & { finish: PatternFinish };

/**
 * Pattern voucher card: an animated WebGL2 field (marble veins, halftone pulse, prism light)
 * that leans toward the pointer. Without WebGL2 the card keeps its base colour.
 */
export const PatternCard = ({
  finish,
  title,
  subtitle,
  label,
  amount,
  className,
}: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const rm = usePrefersReducedMotion();
  // Read per frame, so a corrected value doesn't restart the WebGL setup.
  const rmRef = useRef(rm);
  // Pointer, eased: the shader's own hover response plus a small lean.
  const pointer = useRef({ x: 0.5, y: 0.65, tx: 0.5, ty: 0.65, h: 0, th: 0 });

  useEffect(() => {
    rmRef.current = rm;
    const card = cardRef.current;
    if (!card) return;
    const m = pointer.current;
    return attachCardTilt(card, {
      reducedMotion: rm,
      onMove: (x, y) => {
        m.tx = x;
        m.ty = 1 - y;
        m.th = 1;
      },
      onLeave: () => {
        m.th = 0;
      },
    });
  }, [rm]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const canvas = document.createElement('canvas');
    canvas.className = styles.canvas;
    canvas.setAttribute('aria-hidden', 'true');
    // preserveDrawingBuffer: CardStage samples this canvas to morph between cards.
    const gl = canvas.getContext('webgl2', {
      antialias: false,
      premultipliedAlpha: false,
      alpha: false,
      preserveDrawingBuffer: true,
    });
    if (!gl) return;
    const prog = createFullScreenProgram(
      gl,
      PATTERN_VS,
      PATTERN_PRE + PATTERN_FS[finish],
    );
    if (!prog) {
      loseGl(gl);
      return;
    }
    const u = {
      res: gl.getUniformLocation(prog, 'uRes'),
      time: gl.getUniformLocation(prog, 'uTime'),
      mouse: gl.getUniformLocation(prog, 'uMouse'),
      hover: gl.getUniformLocation(prog, 'uHover'),
      cells: gl.getUniformLocation(prog, 'uCells'),
      line: gl.getUniformLocation(prog, 'uLine'),
    };
    card.prepend(canvas);
    const m = pointer.current;

    let visible = true;
    const io = new IntersectionObserver(
      ([e]) => {
        visible = e.isIntersecting;
      },
      { rootMargin: '100px' },
    );
    io.observe(card);

    const t0 = performance.now();
    const draw = (now: number) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(2, Math.round(card.clientWidth * dpr));
      const h = Math.max(2, Math.round(card.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      m.x += (m.tx - m.x) * 0.1;
      m.y += (m.ty - m.y) * 0.1;
      m.h += (m.th - m.h) * 0.06;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(u.res, w, h);
      gl.uniform1f(u.time, rmRef.current ? 8 : (now - t0) / 1000 + 6);
      gl.uniform2f(u.mouse, m.x, m.y);
      gl.uniform1f(u.hover, m.h);
      // Halftone cells and marble line width scale with the card (design v4).
      gl.uniform1f(
        u.cells,
        Math.max(20, Math.min(42, Math.round(h / dpr / 7.4))),
      );
      gl.uniform1f(u.line, 1.6 * Math.max(0.7, Math.min(3.2, h / 280)));
      drawFullScreen(gl);
      card.dataset.gl = 'on';
    };

    let raf = 0;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (visible) draw(now);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      loseGl(gl);
      canvas.remove();
      delete card.dataset.gl;
    };
  }, [finish]);

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${bebasNeue.variable} ${geistMono.variable} ${
        className ?? ''
      }`}
      style={{ ...INK[finish], background: PATTERN_BG[finish] }}
      role="img"
      aria-label={label}
    >
      {finish === 'prism' && (
        <svg
          className={styles.cut}
          viewBox="0 0 164 100"
          aria-hidden="true"
          data-morph-plate=""
        >
          <rect width="164" height="100" fill="#000" />
          <path
            d={PRISM_WORDMARK_PATH}
            fill="#fff"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      )}
      <ArtCardFace title={title} subtitle={subtitle} amount={amount} />
    </div>
  );
};
